(() => {
  if (customElements.get('bungeroth-collection')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  class BungerothCollection extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.abort = new AbortController();
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.cards = [...this.querySelectorAll('[data-product]')];
      this.eligible = new Set(this.cards.filter(c => Number(c.dataset.price) === 5000000).map(c => Number(c.dataset.product)));
      this.cart = null;
      this.goal = 6;
      try { if (sessionStorage.getItem('bungeroth-pack-goal') === '12') this.goal = 12; } catch (_) {}
      this.busy = false;
      this.onlySelected = false;
      this.filters = this.querySelector('[data-filters]');
      this.root = this.dataset.root.replace(/\/?$/, '/');
      const on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.abort.signal });
      this.querySelectorAll('[data-pack-goal]').forEach(button => on(button, 'click', () => {
        this.goal = Number(button.dataset.packGoal);
        try { sessionStorage.setItem('bungeroth-pack-goal', String(this.goal)); } catch (_) {}
        this.renderGoal(); if (this.cart) this.render();
      }));
      this.renderGoal();
      on(this.filters, 'submit', e => e.preventDefault());
      on(this.filters, 'input', () => this.filter());
      on(this.filters, 'change', () => this.filter());
      on(this.querySelector('[data-reset]'), 'click', () => { this.filters.reset(); this.onlySelected = false; this.filter(); });
      on(this.querySelector('[data-selected]'), 'click', () => { this.onlySelected = !this.onlySelected; this.filter(); });
      on(this.querySelector('[data-retry]'), 'click', () => this.refresh());
      on(this, 'submit', e => {
        const form = e.target.closest('[data-add-form]');
        if (!form) return;
        e.preventDefault();
        this.change(form.closest('[data-product]'), 1);
      });
      on(this, 'click', e => {
        const button = e.target.closest('[data-plus], [data-minus]');
        if (button) this.change(button.closest('[data-product]'), button.hasAttribute('data-minus') ? -1 : 1);
      });
      on(document, 'cart:update', e => {
        if (e.detail?.sourceId !== 'bungeroth-collection') this.refresh();
      });
      on(window, 'pageshow', () => this.refresh());
      on(window, 'focus', () => this.refresh());
      on(document, 'visibilitychange', () => { if (!document.hidden) this.refresh(); });
      if (matchMedia('(max-width: 600px)').matches) this.querySelector('details').open = false;
      this.loadProfiles();
      this.filter();
      this.refresh();
      if (!this.cards.length && new URL(location.href).searchParams.has('page')) {
        const url = new URL(location.href); url.searchParams.delete('page'); location.replace(url.href);
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.started = false; }
    t(key, values = {}) { return Object.entries(values).reduce((text, [k, v]) => text.replaceAll('{' + k + '}', v), this.strings[key] || key); }
    money(cents) { return new Intl.NumberFormat('es-CO', { style: 'currency', currency: this.cart?.currency || 'COP', maximumFractionDigits: 0 }).format(cents / 100); }
    async loadProfiles() {
      try {
        const response = await fetch(this.dataset.profilesUrl);
        if (!response.ok) throw new Error();
        this.profiles = await response.json();
        const profiles = new Set(), notes = new Set();
        this.cards.forEach(card => {
          const p = this.profiles[card.dataset.handle] || {};
          card.profile = p;
          card.querySelector('[data-gender]').textContent = p.gender ? this.t(p.gender) : '';
          const tags = card.querySelector('[data-tags]'); tags.replaceChildren();
          (p.profiles || []).forEach(text => { const span = document.createElement('span'); span.textContent = text; tags.append(span); profiles.add(text); });
          (p.notes || []).forEach(n => notes.add(n));
          card.querySelector('[data-notes]').textContent = (p.notes || []).slice(0,3).join(' · ');
        });
        for (const [field, values] of [['profile',profiles],['note',notes]]) {
          const select = this.filters.elements[field]; select.length = 1;
          [...values].sort((a,b) => a.localeCompare(b,'es')).forEach(value => { select.add(new Option(value, value)); });
        }
        this.querySelector('[data-profiles-status]').hidden = true;
        this.filter();
      } catch (_) {
        this.querySelector('[data-profiles-status]').textContent = this.t('profiles_error');
        ['gender','style','profile','note','occasion'].forEach(key => { this.filters.elements[key].disabled = true; });
      }
    }
    filter() {
      const f = Object.fromEntries(new FormData(this.filters));
      let count = 0;
      this.cards.forEach(card => {
        const p = card.profile || {};
        const haystack = normalize(card.querySelector('[data-title]').dataset.title + ' ' + (p.profiles || []).join(' ') + ' ' + (p.notes || []).join(' '));
        const matches = (!f.query || haystack.includes(normalize(f.query))) && (!f.gender || p.gender === f.gender) && (!f.style || p.styles?.includes(f.style)) && (!f.profile || p.profiles?.includes(f.profile)) && (!f.note || p.notes?.includes(f.note)) && (!f.occasion || p.occasions?.includes(f.occasion)) && (!this.onlySelected || this.quantity(card) > 0);
        card.hidden = !matches;
        if (matches) count++;
      });
      this.querySelector('[data-results]').textContent = this.t(count === 1 ? 'result_one' : 'results', {count});
      this.querySelector('[data-empty]').hidden = count !== 0;
      this.querySelector('[data-reset]').hidden = !Object.values(f).some(Boolean) && !this.onlySelected;
      const selected = this.querySelector('[data-selected]');
      selected.setAttribute('aria-pressed', String(this.onlySelected));
      selected.textContent = this.t(this.onlySelected ? 'all_aromas' : 'selected');
    }
    renderGoal() {
      this.querySelectorAll('[data-pack-goal]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.packGoal) === this.goal)));
      this.querySelector('[data-offer]').textContent = this.t('offer_dynamic', {goal:this.goal, price:this.money(this.goal === 12 ? 21800000 : 11900000)});
      this.querySelector('[data-offer-note]').textContent = this.t('offer_dynamic_note', {goal:this.goal});
      this.querySelector('progress').max = this.goal;
    }
    quantity(card) { return (this.cart?.items || []).filter(item => String(item.variant_id) === card.dataset.variant).reduce((n, item) => n + item.quantity, 0); }
    error(message) {
      const element = this.querySelector('[data-error]');
      element.textContent = message || ''; element.hidden = !message;
      this.querySelector('[data-retry]').hidden = !message;
    }
    async getCart() {
      const response = await fetch(this.root + 'cart.js', {cache:'no-store',headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error();
      return response.json();
    }
    async refresh() {
      if (this.busy || this.refreshing) return;
      this.refreshing = true;
      try { this.cart = await this.getCart(); this.error(''); this.render(); }
      catch (_) { this.error(this.t('cart_error')); }
      finally { this.refreshing = false; }
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.cards.forEach(card => card.querySelectorAll('button').forEach(button => { button.disabled = value || (card.dataset.available === 'false' && !button.hasAttribute('data-minus')); }));
    }
    async change(card, delta) {
      if (this.busy) return;
      this.setBusy(true); this.error('');
      let changed = false;
      try {
        this.cart = await this.getCart();
        const variant = Number(card.dataset.variant);
        const lines = this.cart.items.filter(item => item.variant_id === variant);
        const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el=>el.dataset.sectionId))].slice(0,5);
        const sections = {sections:sectionIds, sections_url:location.pathname};
        let endpoint, body;
        if (delta > 0) { endpoint = 'cart/add.js'; body = {items:[{id:variant,quantity:1}], ...sections}; }
        else {
          if (!lines.length) { this.render(); return; }
          endpoint = 'cart/change.js'; body = {id:lines[0].key,quantity:Math.max(0,lines[0].quantity-1), ...sections};
        }
        const response = await fetch(this.root + endpoint, {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body)});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.t('error'));
        changed = true;
        this.cart = await this.getCart();
        this.render();
        document.dispatchEvent(new CustomEvent('cart:update', {bubbles:true,detail:{resource:this.cart,sourceId:'bungeroth-collection',data:{source:'bungeroth-collection',itemCount:this.cart.item_count,sections:result.sections || {}}}}));
      } catch (e) {
        try { this.cart = await this.getCart(); this.render(); } catch (_) {}
        this.error(changed ? this.t('error') : (e.message || this.t('error')));
      } finally { this.setBusy(false); }
    }
    render() {
      const count = (this.cart.items || []).filter(item=>this.eligible.has(item.product_id) && item.original_price === 5000000).reduce((n,item)=>n+item.quantity,0);
      const remaining = Math.max(0,this.goal-count);
      this.querySelector('[data-progress-title]').textContent = this.t(count > this.goal ? 'many' : count === this.goal ? 'ready' : remaining === 1 ? 'remaining_one' : 'remaining',{count:count > this.goal ? count : remaining,goal:this.goal});
      this.querySelector('[data-count]').textContent = this.t('count',{count,goal:this.goal});
      this.querySelector('progress').value = Math.min(count,this.goal);
      this.querySelector('[data-total]').textContent = this.t('cart_total',{total:this.money(this.cart.total_price)});
      const discount = this.cart.total_discount || 0;
      this.querySelector('[data-discount]').textContent = discount ? this.t('discount',{amount:this.money(discount)}) : count >= 6 ? this.t('discount_pending') : '';
      this.cards.forEach(card => {
        const q = this.quantity(card);
        card.classList.toggle('is-selected',q>0);
        const form = card.querySelector('[data-add-form]'), picker = card.querySelector('[data-quantity]');
        if (form && picker) { form.hidden = q>0; picker.hidden = q===0; picker.querySelector('[data-qty]').textContent = q; }
        const badge = card.querySelector('[data-card-quantity]'); badge.hidden = !q; badge.textContent = this.t('in_cart',{count:q});
      });
      this.filter();
    }
  }
  customElements.define('bungeroth-collection',BungerothCollection);
})();
